"use client";

import { Check, Circle, Loader2, XCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { ArtifactCanvas } from "@/components/chat/ArtifactCanvas";
import { RiskAssessmentWorkspaceReport } from "@/components/risk-assessment/RiskAssessmentWorkspaceReport";
import { fetchWithAuth } from "@/lib/fetchWithAuth";
import type { AgentFocusTarget } from "@/lib/agentRunTransport";
import type {
  ObjectRiskRunStage,
  ObjectRiskRunState,
} from "./useObjectRiskAssessmentRun";
import type { RiskWorkspaceArtifact } from "@/components/risk-assessment/useRiskAssessmentRunController";

const apiUrl = () => process.env.NEXT_PUBLIC_API_URL || "http://localhost:3000";

function StoredFocusImage({
  fileId,
  localUrl,
}: {
  fileId: string;
  localUrl?: string | null;
}) {
  const [remoteUrl, setRemoteUrl] = useState<string | null>(null);

  useEffect(() => {
    if (localUrl) return;
    let active = true;
    let objectUrl: string | null = null;
    const controller = new AbortController();

    void fetchWithAuth(`${apiUrl()}/api/files/${fileId}/content`, {
      signal: controller.signal,
      skipRedirectOn401: false,
    })
      .then((response) => {
        if (!response.ok) throw new Error("focus image unavailable");
        return response.blob();
      })
      .then((blob) => {
        if (!active) return;
        objectUrl = URL.createObjectURL(blob);
        setRemoteUrl(objectUrl);
      })
      .catch(() => {
        if (active) setRemoteUrl(null);
      });

    return () => {
      active = false;
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [fileId, localUrl]);

  const displayUrl = localUrl ?? remoteUrl;

  return displayUrl ? (
    // Blob/object URLs are authenticated runtime resources and cannot use next/image.
    // eslint-disable-next-line @next/next/no-img-element
    <img
      src={displayUrl}
      alt="선택된 분석 객체"
      className="max-h-full max-w-full object-contain"
    />
  ) : (
    <span className="text-[11px] font-semibold text-gray-400">객체 이미지</span>
  );
}

function StageStatus({ stage }: { stage: ObjectRiskRunStage }) {
  if (stage.status === "complete")
    return <Check size={15} className="text-seexr-primary" />;
  if (stage.status === "running")
    return <Loader2 size={15} className="animate-spin text-seexr-primary" />;
  if (stage.status === "error")
    return <XCircle size={15} className="text-red-500" />;
  return <Circle size={15} className="text-gray-300 dark:text-gray-600" />;
}

export function ObjectRiskAssessmentRunPanel({
  state,
  focusTarget,
  artifact,
  sessionId,
  stages,
  progressPercent,
  error,
  localFocusUrl,
  onCancel,
}: {
  state: ObjectRiskRunState;
  focusTarget: AgentFocusTarget | null;
  artifact: RiskWorkspaceArtifact | null;
  sessionId: string | null;
  stages: ObjectRiskRunStage[];
  progressPercent: number;
  error: string | null;
  localFocusUrl?: string | null;
  onCancel: () => void;
}) {
  if (state === "idle") return null;

  const busy = state === "loading" || state === "uploading" || state === "generating";

  return (
    <section className="space-y-5">
      {focusTarget && (
        <div className="rounded-[24px] border border-seexr-primary/20 bg-seexr-primary/[0.045] p-4">
          <div className="flex items-center gap-4">
            <div className="flex h-24 w-28 shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-gray-200 bg-[linear-gradient(45deg,#f2f2f2_25%,transparent_25%,transparent_75%,#f2f2f2_75%),linear-gradient(45deg,#f2f2f2_25%,transparent_25%,transparent_75%,#f2f2f2_75%)] bg-[length:12px_12px] bg-[position:0_0,6px_6px] dark:border-gray-700 dark:bg-black/20">
              <StoredFocusImage
                fileId={focusTarget.focusFileId}
                localUrl={localFocusUrl}
              />
            </div>
            <div className="min-w-0">
              <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-seexr-primary">
                분석 대상
              </p>
              <h2 className="mt-1 truncate text-lg font-bold text-gray-950 dark:text-gray-50">
                {focusTarget.label || focusTarget.textPrompt || "선택 객체"}
              </h2>
              <p className="mt-1 text-xs leading-5 text-gray-500 dark:text-gray-400">
                {focusTarget.selectionMethod} 선택 · SAM confidence{" "}
                {(focusTarget.score * 100).toFixed(1)}%
              </p>
              <p className="text-xs leading-5 text-gray-400">
                전체 현장은 맥락으로, 이 객체는 위험 분석의 중심으로 사용됩니다.
              </p>
            </div>
          </div>
        </div>
      )}

      {busy && (
        <div className="rounded-[24px] border border-gray-200 bg-white/70 p-5 dark:border-gray-800 dark:bg-white/[0.025]">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h2 className="text-sm font-bold text-gray-900 dark:text-gray-100">
                {state === "loading"
                  ? "저장된 평가를 불러오는 중"
                  : state === "uploading"
                    ? "원본 사진과 선택 객체를 안전하게 전송 중"
                    : "선택 객체 중심으로 위험성평가 생성 중"}
              </h2>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                현재 단계가 바뀌어도 화면 조작을 잠그지 않고 진행 상태를 계속 표시합니다.
              </p>
            </div>
            {state !== "loading" && (
              <button
                type="button"
                onClick={onCancel}
                className="shrink-0 rounded-xl border border-gray-200 px-3 py-2 text-xs font-semibold text-gray-500 transition hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-white/[0.04]"
              >
                취소
              </button>
            )}
          </div>

          <div className="mt-4 h-1.5 overflow-hidden rounded-full bg-gray-100 dark:bg-white/[0.07]">
            <div
              className="h-full rounded-full bg-seexr-primary transition-[width] duration-300"
              style={{ width: `${Math.max(4, progressPercent)}%` }}
            />
          </div>
          <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
            {stages.map((stage) => (
              <div
                key={stage.id}
                className="flex items-center gap-2 rounded-xl border border-gray-200/80 px-3 py-2.5 dark:border-gray-800"
              >
                <StageStatus stage={stage} />
                <span className="text-[11px] font-semibold text-gray-600 dark:text-gray-300">
                  {stage.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {(state === "failed" || state === "cancelled") && (
        <div className="rounded-[24px] border border-red-200 bg-red-50/70 p-4 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/20 dark:text-red-300">
          {error || "객체 중심 위험성평가를 완료하지 못했습니다."}
        </div>
      )}

      {state === "ready" && artifact && (
        <div className="min-h-[760px] overflow-hidden rounded-[24px] border border-gray-200 bg-white dark:border-gray-800 dark:bg-[#171719]">
          <ArtifactCanvas
            key={`${artifact.key}:${artifact.messageId ?? "object-risk"}`}
            title={artifact.title}
            type="risk_assessment"
            xmlContent={artifact.xmlContent}
            sessionId={sessionId}
            messageId={artifact.messageId}
            onClose={() => undefined}
            showCloseButton={false}
            fallbackAttachmentFileIds={
              focusTarget ? [focusTarget.originalFileId] : artifact.attachmentFileIds
            }
            sourceDocuments={artifact.sourceDocuments}
            RiskAssessmentReport={RiskAssessmentWorkspaceReport}
            contentWidthClassName="max-w-none"
          />
        </div>
      )}

      {state === "ready" && !artifact && (
        <div className="rounded-[24px] border border-gray-200 p-5 text-sm text-gray-500 dark:border-gray-800">
          보고서 저장 결과를 동기화하고 있습니다.
        </div>
      )}
    </section>
  );
}
